import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Adjunto, type Tipologia, type Unidad, type UnidadDetalle } from "@/lib/api";
import { TIPOS_CON_TIPOLOGIA, aPayloadUnidad, compartidaCon, formUnidad, opcionesTipologia, superficieTotal, validarUnidad, type ErroresUnidad, type FormUnidad, type PayloadUnidad } from "@/lib/unidades";

type Dialogo = "editar" | "borrar" | "borrarTipologia" | null;
const SIN_ERRORES: ErroresUnidad = {};

/** Secciones de archivos de la ficha: los internos (no los ve el fiduciante), el plano y los renders (se ven en Proyecto)
 * y los renders de su tipología (se ven en Proyecto en cada depto igual). */
export type SeccionArchivos = "adjuntos" | "plano" | "renders" | "tipologia";
const RUTA_SUBIDA: Record<Exclude<SeccionArchivos, "tipologia">, string> = { adjuntos: "adjuntos", plano: "plano/adjuntos", renders: "renders/adjuntos" };

/** Lógica de la ficha de una unidad (sin JSX): edición, baja y adjuntos. Sólo el admin escribe. */
export function useUnidadTab() {
  const { fid, membresia } = useContextoApp();
  const { id } = useParams();
  const navegar = useNavigate();
  const ruta = `/api/fideicomisos/${fid}/unidades/${Number(id)}`;
  const esAdmin = membresia.rol === "admin";

  const ficha = useApiResource<UnidadDetalle>(ruta);
  const unidad = ficha.data ?? null;
  // Tipologías: sólo las llevan deptos, locales y oficinas (las que tienen renders).
  const conTipologia = unidad !== null && TIPOS_CON_TIPOLOGIA.includes(unidad.tipo);
  const tipologias = useApiResource<Tipologia[]>(conTipologia ? `/api/fideicomisos/${fid}/tipologias` : null);
  const tipologia = tipologias.data?.find((t) => t.id === unidad?.tipologia_id) ?? null;
  const recargarTodo = () => {
    ficha.recargar();
    tipologias.recargar();
  };

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormUnidad>(() => formUnidad());
  const [mostrarErrores, setMostrarErrores] = useState(false);

  const accionEditar = useAsyncAction((datos: PayloadUnidad) => api.patch<Unidad>(ruta, datos));
  const accionBorrar = useAsyncAction(() => api.delete<void>(ruta).then(() => true));
  // La última sección de archivos que se tocó: ahí se muestran el "subiendo" y el error.
  const [seccion, setSeccion] = useState<SeccionArchivos>("adjuntos");
  const accionSubir = useAsyncAction((clase: SeccionArchivos, archivo: File) => {
    const f = new FormData();
    f.append("archivo", archivo);
    const destino = clase === "tipologia" ? `/api/fideicomisos/${fid}/tipologias/${tipologia?.id}/renders/adjuntos` : `${ruta}/${RUTA_SUBIDA[clase]}`;
    return api.upload<Adjunto>(destino, f);
  });
  // Elegir otra tipología (o ninguna) se guarda al toque; escribir una nueva la crea con esta unidad y sus iguales.
  const accionTipologia = useAsyncAction((tipologiaId: number | null) => api.patch<Unidad>(ruta, { tipologia_id: tipologiaId }));
  const accionCrearTipologia = useAsyncAction((nombre: string) => api.post<Tipologia>(`/api/fideicomisos/${fid}/tipologias`, { nombre, unidad_id: Number(id) }));
  const accionBorrarTipologia = useAsyncAction(() => api.delete<void>(`/api/fideicomisos/${fid}/tipologias/${tipologia?.id}`).then(() => true));
  const accionBorrarAdjunto = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  const accionUsar = useAsyncAction(() =>
    api.post<{ actualizadas: number }>(`/api/fideicomisos/${fid}/unidades/precio-lista/usar-sugerido`, { unidad_ids: [Number(id)] }),
  );
  const errores = validarUnidad(form);
  const cerrar = () => setDialogo(null);

  const abrirEditar = () => {
    if (!unidad) return;
    setForm(formUnidad(unidad));
    setMostrarErrores(false);
    accionEditar.limpiarError();
    setDialogo("editar");
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (Object.keys(errores).length > 0) return;
    if (await accionEditar.run(aPayloadUnidad(form))) {
      cerrar();
      ficha.recargar();
    }
  };

  const usarSugerido = async () => {
    if (await accionUsar.run()) ficha.recargar();
  };

  const confirmarBorrar = async () => {
    if (await accionBorrar.run()) void navegar("/unidades");
  };

  const subir = async (clase: SeccionArchivos, archivo: File) => {
    setSeccion(clase);
    accionBorrarAdjunto.limpiarError();
    if (await accionSubir.run(clase, archivo)) recargarTodo();
  };

  const elegirTipologia = async (valor: string) => {
    accionCrearTipologia.limpiarError();
    if (await accionTipologia.run(valor === "" ? null : Number(valor))) recargarTodo();
  };

  const crearTipologia = async (nombre: string) => {
    accionTipologia.limpiarError();
    if (await accionCrearTipologia.run(nombre)) recargarTodo();
  };

  const confirmarBorrarTipologia = async () => {
    if (await accionBorrarTipologia.run()) {
      cerrar();
      recargarTodo();
    }
  };

  const borrarAdjunto = async (clase: SeccionArchivos, a: Adjunto) => {
    setSeccion(clase);
    accionSubir.limpiarError();
    if (await accionBorrarAdjunto.run(a.id)) recargarTodo();
  };

  /** Lo que necesita cada `Adjuntos` de la ficha: los archivos internos, el plano y los renders. */
  const archivos = (clase: SeccionArchivos) => ({
    onSubir: (archivo: File) => subir(clase, archivo),
    onBorrar: (a: Adjunto) => borrarAdjunto(clase, a),
    subiendo: accionSubir.loading && seccion === clase,
    error: seccion === clase ? (accionSubir.error ?? accionBorrarAdjunto.error) : null,
  });

  return {
    esAdmin,
    unidad,
    cargando: ficha.loading && !ficha.data,
    errorCarga: ficha.data ? null : (ficha.error?.message ?? null),
    dialogo,
    cerrar,
    abrirEditar,
    form,
    cambiar: <K extends keyof FormUnidad>(campo: K, valor: FormUnidad[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    total: superficieTotal(form),
    guardar,
    guardando: accionEditar.loading,
    errorGuardar: accionEditar.error,
    abrirBorrar: () => {
      accionBorrar.limpiarError();
      setDialogo("borrar");
    },
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    archivos,
    // Precio sugerido → precio de lista (sólo si son distintos).
    puedeUsarSugerido: esAdmin && unidad?.precio_sugerido_usd != null && unidad.precio_sugerido_usd !== unidad.precio_lista_usd,
    usarSugerido,
    usando: accionUsar.loading,
    errorUsar: accionUsar.error,
    // Tipología: deptos iguales que comparten los renders.
    conTipologia,
    tipologia,
    opcionesTipologia: opcionesTipologia(tipologias.data ?? [], tipologia !== null),
    compartidaCon: tipologia && unidad ? compartidaCon(tipologia, unidad.id) : "",
    elegirTipologia,
    crearTipologia,
    guardandoTipologia: accionTipologia.loading || accionCrearTipologia.loading,
    errorTipologia: accionTipologia.error ?? accionCrearTipologia.error ?? tipologias.error?.message ?? null,
    abrirBorrarTipologia: () => {
      accionBorrarTipologia.limpiarError();
      setDialogo("borrarTipologia");
    },
    confirmarBorrarTipologia,
    borrandoTipologia: accionBorrarTipologia.loading,
    errorBorrarTipologia: accionBorrarTipologia.error,
  };
}
